<template>
  <b-row class="m-0">
    <b-col class="container__create-stack">
      <div>
        <base-bread-crumb :items="breadcrumbs" />
        <h5 class="font-weight-bold text__stack-component mb-4">
          {{ isEditMode ? 'Edit Stack' : 'Create New Stack' }}
        </h5>
      </div>

      <b-form class="form__create-stack" @submit.prevent="submit">
        <!-- title -->
        <div>
          <label for="stack-title" class="mb-3">
            Title
            <span class="text-danger">*</span>
          </label>

          <b-form-input
            id="stack-title"
            v-model="$v.form.title.$model"
            :class="{
              'is-invalid': $v.form.title.$error
            }"
            class="input__stack-title text__stack-title"
            :disabled="isLoading"
            placeholder="Insert stack title"
          >
          </b-form-input>
          <b-form-invalid-feedback
            v-if="!$v.form.title.required"
            ref="input__error-required"
            class="text_feedback"
          >
            Title is required
          </b-form-invalid-feedback>
        </div>

        <!-- description -->
        <div>
          <label for="stack-description" class="mb-3">
            Description
            <span class="text-danger">*</span>
          </label>

          <b-form-textarea
            id="stack-description"
            v-model="$v.form.description.$model"
            rows="6"
            placeholder="Insert your description"
            :class="{
              'text-textarea__required': $v.form.description.$error
            }"
            class="input__stack-description text__stack-description"
            :disabled="isLoading"
          >
          </b-form-textarea>
          <div v-if="$v.form.description.$error" ref="input__error-required" class="text_feedback">
            Description is required
          </div>
        </div>

        <!-- image -->
        <div>
          <base-upload-media
            id="input__stack_image"
            ref="imageUploader"
            key="imageUploader"
            header="Upload image"
            :required="isSubmitted"
            :reference-meta="referenceMeta"
            field-upload="image"
            :file.sync="formMedia.image"
            :has-file.sync="file.image"
            accepted-files=".jpeg, .jpg, .png, .gif"
          >
          </base-upload-media>
          <hr class="mt-u8" />
        </div>

        <!-- learning units -->
        <div>
          <stack-learning-unit-selector
            ref="learningUnitSelector"
            v-model="form.learning_units"
            :is-deletable="isDeletable"
            :is-published="isPublished"
            @delete="deleteFile"
          >
          </stack-learning-unit-selector>
          <div
            v-if="$v.form.learning_units.$error"
            ref="input__error-required"
            class="text_feedback"
          >
            Please select micro-credentials.
          </div>
          <hr class="mt-u8" />
        </div>

        <!-- status -->
        <div>
          <label for="input__mc-overview" class="h5 font-weight-bold mb-4"> Stack Status </label>
          <b-form-group id="input__radio-stack-status" class="mb-0">
            <b-form-radio v-model="form.status" name="stack-status" value="DRAFT" class="mb-4">
              <h5>Draft</h5>
              <span class="mt-2">
                The stack will not be displayed. It can be revised anytime before publication.
              </span>
            </b-form-radio>
            <b-form-radio v-model="form.status" name="stack-status" value="PUBLISHED" class="mb-4">
              <h5>Published</h5>
              <span class="mt-2">
                Anyone can access and view the stack, with only published micro-credentials
                displayed.
              </span>
            </b-form-radio>
            <hr class="mt-u8 mb-0" />
          </b-form-group>
        </div>

        <div v-if="$v.form.status.$error" ref="input__error-required" class="text_feedback">
          status is required
        </div>
      </b-form>
    </b-col>
  </b-row>
</template>

<script>
import BaseUploadMedia from '@/components/base-components/BaseUploadMedia';
import StackLearningUnitSelector from './StackLearningUnitSelector.vue';
import Stack from '@/models/Stack';
import BaseBreadCrumb from '@/components/base-components/BaseBreadCrumb.vue';

import { required } from 'vuelidate/lib/validators';

export default {
  components: {
    BaseUploadMedia,
    StackLearningUnitSelector,
    BaseBreadCrumb
  },
  props: {
    stack: {
      type: Object,
      default: () => ({})
    },
    isDeletable: {
      type: Boolean,
      default: true
    },
    isPublished: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create'
    }
  },
  data() {
    return {
      isSubmitted: false,
      isLoading: false,
      isShowModal: false,
      fileToDelete: null,
      tempIndex: null,
      form: {
        title: '',
        description: '',
        image: [],
        status: 'DRAFT',
        learning_units: []
      },
      formMedia: {
        image: []
      },

      file: {
        image: false
      },
      referenceMeta: {
        ref: 'stack',
        refId: null
      }
    };
  },
  validations: {
    form: {
      title: {
        required
      },
      description: {
        required
      },
      status: {
        required
      },
      learning_units: {
        required,
        minPublishedUnits(value) {
          // Skip validation if no learning units or stack is in draft mode
          const isStackInDraftMode = this.form.status !== 'PUBLISHED';
          if (!value || isStackInDraftMode) {
            return true;
          }

          // Count learning units that are in published state
          const publishedUnits = value.filter((unit) => unit.status === 'published');

          // Ensure we have minimum required published units for a published stack
          return publishedUnits.length >= 3;
        }
      }
    }
  },
  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },
    breadcrumbs() {
      const isCreateStackMode = this.$route.name === 'create-stack';

      const breadcrumb = [
        {
          key: 'stack-managing',
          name: 'Stack Managing',
          to: {
            name: 'stack-managing'
          }
        },
        {
          key: 'stack-action',
          name: `${isCreateStackMode ? 'Create New Stack' : 'Edit Stack'} `,
          active: true
        }
      ];

      return breadcrumb;
    }
  },
  mounted() {
    this.initFormData();
  },
  methods: {
    initFormData() {
      if (this.mode == 'edit') {
        this.$set(this, 'form', {
          title: this.stack?.title,
          description: this.stack?.description,
          learning_units: this.stack?.learning_units,
          status: this.stack?.status
        });
        this.$set(this, 'formMedia', {
          image: this.stack?.image ? [this.stack?.image] : []
        });
      }

      this.$nextTick(() => this.$v.$reset());
      this.$nextTick(() => {
        if (this.$refs.imageUploader) {
          this.$refs.imageUploader.resetUploader();
        }
      });
    },

    deleteFile() {
      if (this.formMedia.image && this.formMedia.image.id) {
        this.fileToDelete = this.formMedia.image;
        this.form.image = null;
      } else {
        this.form.image = null;
      }
    },

    activeModal() {
      this.isShowModal = true;
    },

    confirmCancel() {
      this.isShowModal = false;
      this.$emit('delete', { id: this.stack.id, index: this.tempIndex });
    },

    async submit() {
      try {
        if (this.isLoading) {
          return;
        }
        this.isLoading = true;
        this.isSubmitted = true;
        this.$v.$touch();

        if (this.$v.$invalid || !this.file.image) {
          this.isLoading = false;
          return this.$emit('submit:stack', null);
        }
        const lu_id = this.form.learning_units.map((lu) => lu.id);
        const formData = {
          ...this.form,
          learning_units: lu_id
        };
        //call model to create stack
        if (this.formMedia.image.length) {
          delete formData.image;
        }
        let response = null;
        if (this.mode == 'edit') {
          response = await Stack.api().update(this.stack.id, formData);
        } else {
          response = await Stack.api().create(formData);
        }
        const {
          response: { data }
        } = response;

        await this.uploadFiles(data.id);

        this.$toast.success('Stack created successfully');
        // push to stack list page
        this.isLoading = false;
        this.isSubmitted = false;
        this.$emit('submit:stack', formData);
      } catch (error) {
        this.isLoading = false;
        this.isSubmitted = false;
        this.$toast.error('Failed to save stack');
        this.$emit('submit:stack', null);
      }
    },

    async uploadFiles(relatedId) {
      let data = {
        ...this.form
      };
      this.$set(this.referenceMeta, 'refId', relatedId);

      if (this.$refs.imageUploader.$children[0].hasAnyDeletedFiles()) {
        await this.$refs.imageUploader.$children[0].deleteRemovedFiles();
      }
      if (this.$refs.imageUploader.$children[0].hasAnyQueuedFiles()) {
        const [image] = await this.$refs.imageUploader.$children[0].upload();

        data.image = image;
      }
      if (this.fileToDelete) {
        await this.removeFiles([this.fileToDelete]);
      }
      this.fileToDelete = null;

      this.$v.$reset();
      this.isLoading = false;

      return data.image;
    },

    async resetForm() {
      Object.assign(this.form, {
        title: '',
        description: '',
        attachment: null
      });

      this.$nextTick(() => {
        if (this.$refs.attachmentUploader) {
          this.$refs.attachmentUploader.buffer = [];
        }
        this.$v.$reset();
      });
    },

    async removeFiles(files) {
      for (const file of files) {
        try {
          this.$emit('remove:file', file.id);
        } catch (error) {
          console.error(error);
          throw error;
        }
      }
    }
  }
};
</script>

<style lang="scss">
.container__create-stack {
  display: flex;
  flex-direction: column;
  width: 100%;

  .text__stack-component {
    text-transform: capitalize;
  }

  .form__create-stack {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    .button__delete-stack {
      color: var(--danger);
    }
  }

  .text-editor__required {
    .ql-container.ql-snow {
      background: #ffffff;
      border: 1px solid #f83245;
      border-radius: 0 0 4.64px 4.64px;
    }

    .ql-toolbar.ql-snow {
      background: #ffffff;
      border: 1px solid #f83245;
      border-radius: 4.64px 4.64px 0 0;
      border-bottom: none;
    }
  }

  .text_feedback {
    width: 100%;
    margin-top: 0.25rem;
    font-size: 80%;
    color: #f83245;
  }
}

.text__stack-description {
  resize: none;
}

.form-control.text-textarea__required {
  border-color: #f83245;
}

.button__create-stack {
  display: flex;
  align-items: center;
}

.button__stack-delete {
  color: var(--danger);
}
</style>
